/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React from 'react'
import Text from '../../Text'
import Button from '../../Button'
import ProgressHeader from '../components/ProgressHeader'
import OptionCard from '../components/OptionCard'
import SimpleLeagueIcon from '../icons/SimpleLeagueIcon'
import ComplexLeagueIcon from '../icons/ComplexLeagueIcon'
import { useOnboardingWizard } from '../context/OnboardingWizardContext'
import colors from '@sportninja/common/constants/appColors'
import { trackOnboardingLeagueLayout } from 'src/utils/analytics'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const OptionsContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY_300};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

/**
 * League Setup step component - Step 1 of the onboarding wizard
 * Allows users to choose between Simple League and Complex League
 */
const LeagueSetup: React.FC = () => {
  const { state, setLeagueType, nextStep, skipWizard, contactSpecialist } =
    useOnboardingWizard()

  const handleLeagueTypeSelect = (type: 'simple' | 'complex') => {
    console.log(`🔄 LeagueSetup: User selected ${type} league type`)
    
    // Track the league layout choice
    const choice = type === 'simple' ? 'simple_league' : 'specialist'
    trackOnboardingLeagueLayout(choice)
    
    setLeagueType(type)
  }

  const handleNext = () => {
    if (state.leagueType === 'complex') {
      contactSpecialist()
    } else {
      nextStep()
    }
  }

  return (
    <StepContainer>
      <ProgressHeader currentStep={1} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            Choose Your League Layout
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Choose your league structure so we can guide you through the right
            setup.
          </Text>
        </TitleSection>

        <OptionsContainer>
          <OptionCard
            id='simple_league'
            icon={
              <SimpleLeagueIcon
                color={
                  state.leagueType === 'simple'
                    ? colors.PRIMARY_300
                    : colors.NEUTRAL_100
                }
              />
            }
            title='Simple League'
            description='One level organization managing one or more sports and competitions.'
            isSelected={state.leagueType === 'simple'}
            onClick={() => handleLeagueTypeSelect('simple')}
          />

          <OptionCard
            id='complex_league'
            icon={
              <ComplexLeagueIcon
                color={
                  state.leagueType === 'complex'
                    ? colors.PRIMARY_300
                    : colors.NEUTRAL_100
                }
                size={32}
              />
            }
            title='Onboarding with Specialist'
            description='If you’re running a multi-level organization, we recommend connecting with an Onboarding Specialist.'
            isSelected={state.leagueType === 'complex'}
            onClick={() => handleLeagueTypeSelect('complex')}
          />
        </OptionsContainer>

        <ActionsContainer>
          <Button
            text={
              state.leagueType === 'complex' ? 'BOOK ONBOARDING CALL' : 'NEXT'
            }
            onClick={handleNext}
            variant={'primary'}
            uppercase
            disabled={!state.leagueType}
            style={css`
              width: 100%;
            `}
          />

          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default LeagueSetup
